import React, { useMemo } from "react"
import { Cell, flexRender } from "@tanstack/react-table"
import { useSortable } from "@dnd-kit/sortable"

import {
  TableVisualizationColumnDef,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { getCommonPinningOrderingStyles } from "../../utils"
import { highlightCell } from "~/ui-rtk/utils/table"

type TBodyCellProps = {
  cell: Cell<TMetric, unknown>
  className?: string
  demoMode?: boolean
  highlight?: boolean
  avgData?: Record<string, Record<string, number>>
}

export const BodyCell: React.FC<TBodyCellProps> = ({
  cell,
  className,
  demoMode,
  highlight = false,
  avgData,
}) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  })

  const columnConfig = useMemo(
    () => cell.column.columnDef as TableVisualizationColumnDef,
    [cell],
  )

  const shouldBlur =
    columnConfig.blurInDemo ||
    cell.column.id.includes("name") ||
    cell.column.id.includes("caption") ||
    cell.column.id.includes("preview") ||
    cell.column.id.includes("display") ||
    cell.column.id.includes("title") ||
    cell.column.id.includes("handle")

  const columnKey = useMemo(() => columnConfig.key, [columnConfig])

  const val = useMemo(() => cell.getValue(), [cell])

  const classNameWithAction = useMemo(
    () =>
      cn(
        "p-system-md text-sm text-ellipsis hover:z-40",
        demoMode && shouldBlur && "blur-md",
        className,
        highlight && avgData?.[columnKey]
          ? highlightCell(val as number, avgData[columnKey])
          : undefined,
        highlight && columnConfig.highlightFn
          ? columnConfig.highlightFn(val as number)
          : undefined,
        {
          "z-10": isDragging || cell.column.getIsPinned(),
          "bg-basic-black": !!cell.column.getIsPinned(),
        },
      ),
    [cell, className, avgData, isDragging, demoMode, shouldBlur],
  )

  const tdStyles = useMemo(() => {
    const { minSize, percentSize } = columnConfig

    if (percentSize) {
      return {
        maxWidth: percentSize,
        minWidth: `${minSize ? `${minSize}px` : `${percentSize}`}`,
        width: percentSize,
        ...getCommonPinningOrderingStyles(cell.column, isDragging, transform),
      }
    }
    return {
      maxWidth: `${cell.column.getSize() ? `${cell.column.getSize()}px` : "unset"}`,
      minWidth: `${cell.column.getSize() ? `${cell.column.getSize()}px` : "unset"}`,
      ...getCommonPinningOrderingStyles(cell.column, isDragging, transform),
    }
  }, [cell, isDragging, transform])

  return (
    <td className={classNameWithAction} style={tdStyles} ref={setNodeRef}>
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  )
}
